/**
 * NovaSynth user profile.
 *
 * Persistence is two-tier:
 *   1. Account-tied server store (DynamoDB, keyed by Cognito sub) via /invocations
 *      mode:"profile" - the source of truth that follows the account across
 *      browsers/devices/origins.
 *   2. localStorage cache - instant next-paint + offline/local-dev fallback.
 *
 * Use loadProfile()/persistProfile() (async, server-first) in app flows; the
 * sync get/save helpers below operate on the localStorage cache only.
 */

import { fetchProfileRemote, saveProfileRemote } from "./agentcore";

export interface AlertTriggers {
  highThreat: boolean;
  newProduct: boolean;
  pricingChange: boolean;
  executiveHire: boolean;
}

export interface NovaSynthProfile {
  companyName: string;
  industry: string;
  companySize: string;
  role: string;
  competitors: string[];
  signals: { video: boolean; document: boolean; web: boolean; news: boolean };
  depth: "deep";
  notificationFrequency: "realtime" | "daily" | "weekly";
  emailAlerts: boolean;
  alertEmail: string;
  alertTriggers: AlertTriggers;
  slackWebhook: string;
  // Optional richer personalization (Task F) - sharpens the persona-driven threat
  // read. All optional + progressive; the backend persona scorer tolerates
  // missing/extra keys. Persisted in the same JSON blob - no backend change.
  icp?: string;
  valueProps?: string;
  differentiators?: string;
  winLossNotes?: string;
  targetSegments?: string;
}

const PROFILE_KEY = "novasynth_profile";
const ONBOARDING_KEY = "novasynth_onboarding_complete";

export function getProfile(): NovaSynthProfile | null {
  try {
    const raw = localStorage.getItem(PROFILE_KEY);
    return raw ? (JSON.parse(raw) as NovaSynthProfile) : null;
  } catch {
    return null;
  }
}

export function saveProfile(profile: NovaSynthProfile): void {
  localStorage.setItem(PROFILE_KEY, JSON.stringify(profile));
}

export function isOnboardingComplete(): boolean {
  return localStorage.getItem(ONBOARDING_KEY) === "true";
}

export function markOnboardingComplete(): void {
  localStorage.setItem(ONBOARDING_KEY, "true");
}

export interface LoadProfileResult {
  onboarded: boolean;
  profile: NovaSynthProfile | null;
  source: "server" | "local";
}

/**
 * Load the user's profile - account-tied server store first, falling back to the
 * localStorage cache when the server is unreachable / not configured (local dev).
 * On a successful server read the cache is hydrated so the next paint is instant.
 */
export async function loadProfile(idToken: string): Promise<LoadProfileResult> {
  try {
    const res = await fetchProfileRemote(idToken);
    if (res.ok) {
      const profile = (res.profile as NovaSynthProfile | null) ?? null;
      if (profile) saveProfile(profile);
      if (res.onboarded) markOnboardingComplete();
      return { onboarded: Boolean(res.onboarded), profile, source: "server" };
    }
  } catch {
    // fall through to the localStorage cache
  }
  return { onboarded: isOnboardingComplete(), profile: getProfile(), source: "local" };
}

/**
 * Persist the profile to BOTH the account-tied server store and the localStorage
 * cache. The cache is updated synchronously so the user is never blocked; the
 * account copy syncs best-effort when the runtime is reachable.
 */
export async function persistProfile(idToken: string, profile: NovaSynthProfile): Promise<void> {
  saveProfile(profile);
  markOnboardingComplete();
  try {
    await saveProfileRemote(idToken, profile);
  } catch {
    // best-effort - cache already holds the latest; re-syncs on next save
  }
}

/**
 * Best-effort read of the `email` claim from a Cognito ID token (JWT), used to
 * default the alert-email field to the user's signup email without forcing them
 * to re-type it. Pure client-side convenience read - we do NOT trust this for
 * any security decision (the backend re-verifies the token). Returns "" on any
 * malformed/absent token so callers can treat it as "no default".
 */
export function emailFromIdToken(idToken: string | null | undefined): string {
  if (!idToken) return "";
  try {
    const payload = idToken.split(".")[1];
    if (!payload) return "";
    // base64url -> base64, then decode + UTF-8 unescape for non-ASCII emails.
    const b64 = payload.replace(/-/g, "+").replace(/_/g, "/");
    const json = decodeURIComponent(
      atob(b64)
        .split("")
        .map((c) => "%" + c.charCodeAt(0).toString(16).padStart(2, "0"))
        .join(""),
    );
    const claims = JSON.parse(json) as { email?: unknown };
    return typeof claims.email === "string" ? claims.email : "";
  } catch {
    return "";
  }
}

export function defaultProfile(): NovaSynthProfile {
  return {
    companyName: "",
    industry: "",
    companySize: "",
    role: "",
    competitors: [],
    signals: { video: true, document: true, web: true, news: true },
    depth: "deep",
    notificationFrequency: "realtime",
    emailAlerts: false,
    alertEmail: "",
    alertTriggers: { highThreat: true, newProduct: true, pricingChange: true, executiveHire: true },
    slackWebhook: "",
  };
}
